import { AnalyticsWindow } from "./analytics-window"

declare let window: AnalyticsWindow

interface OptimizelyState {
  id: string
  campaignName: string
  isInCampaignHoldback: boolean
  experiment: {
    id: string
    name: string
  }
  variation: {
    id: string
    name: string
  }
}

interface OptimizelyData {
  "Is Optimizely active"?: boolean
  "Optimizely experiments"?: string[]
  "Optimizely variations"?: string[]
}

function getOptimizelyData(): OptimizelyData {
  if (!window.optimizely || !window.optimizely.get) {
    return {}
  }

  const activeCampaignStates: OptimizelyState[] = Object.values(
    window.optimizely.get("state").getCampaignStates({ isActive: true })
  )

  const isActive = activeCampaignStates.length > 0

  const experiments = activeCampaignStates.map(state =>
    state.experiment.name === state.campaignName
      ? state.experiment.name
      : `${state.campaignName} - ${state.experiment.name}`
  )

  const variations = activeCampaignStates.map(state => {
    const experimentName =
      state.experiment.name === state.campaignName
        ? state.experiment.name
        : `${state.campaignName} - ${state.experiment.name}`
    return `${experimentName} - ${state.variation.name} ${state.isInCampaignHoldback ? "- holdback" : ""}`
  })

  const baseResult = {
    "Is Optimizely active": isActive,
  }

  return isActive
    ? {
        ...baseResult,
        "Optimizely experiments": experiments,
        "Optimizely variations": variations,
      }
    : baseResult
}

function sendEventToOptimizely(
  eventName: string,
  additionalProperties: Record<string, string | boolean> = {}
) {
  if (window.optimizely.get) {
    window["optimizely"].push({
      type: "event",
      eventName: eventName,
      properties: {
        "Page url": window.location.href,
        "Page path": window.location.pathname,
        ...additionalProperties,
      },
    })
  }
}

async function waitOptimizelyData(): Promise<OptimizelyData> {
  const maxRetries = 30
  const retryInterval = 100
  async function wait(t: number) {
    return new Promise(resolve => setTimeout(resolve, t))
  }

  let retries = 0
  while (retries < maxRetries) {
    if (window.optimizely && window.optimizely.get) {
      return getOptimizelyData()
    }
    await wait(retryInterval)
    retries++
  }
  return {}
}

export { getOptimizelyData, sendEventToOptimizely, waitOptimizelyData }
export default waitOptimizelyData
